import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Search, Sparkles, Hammer, Wrench, Zap, Trees, Car, Heart, Scissors,
  Truck, Paintbrush, Wind, Camera, GraduationCap, Dog, Dumbbell, MoreHorizontal,
  Loader2, Home, Droplets, Waves, Sofa, Square, Building, Layers, Fence,
  TreePine, Circle, Anchor, Ship, Bike, Hand, Palette, Flower, Leaf,
  Snowflake, Trash2, Package, Box, Key, Bug, DoorOpen, Video, Music,
  UtensilsCrossed, Calendar, PawPrint, BookOpen, Monitor, Smartphone, Wifi,
  Globe, Calculator
} from "lucide-react";

const iconMap = {
  Sparkles, Hammer, Wrench, Zap, Trees, Car, Heart, Scissors,
  Truck, Paintbrush, Wind, Camera, GraduationCap, Dog, Dumbbell, MoreHorizontal,
  Home, Droplets, Waves, Sofa, Square, Building, Layers, Fence,
  TreePine, Circle, Anchor, Ship, Bike, Hand, Palette, Flower, Leaf,
  Snowflake, Trash2, Package, Box, Key, Bug, DoorOpen, Video, Music,
  UtensilsCrossed, Calendar, PawPrint, BookOpen, Monitor, Smartphone, Wifi,
  Globe, Calculator
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        toast.error("Failed to load categories");
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.subcategories?.some(sub => sub.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E40AF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto" data-testid="categories-page">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-gray-900 mb-4">
            All Service Categories
          </h1>
          <p className="text-gray-600 mb-8">
            Browse all {categories.length} service categories available on BQL
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search categories or services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-full"
              data-testid="category-search"
            />
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCategories.map((category) => {
            const IconComponent = iconMap[category.icon] || MoreHorizontal;
            return (
              <Link
                key={category.id}
                to={`/professionals?category=${category.id}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-[#1E40AF]/30 transition-all group"
                data-testid={`category-${category.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1E40AF]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1E40AF] transition-colors">
                    <IconComponent className="w-6 h-6 text-[#1E40AF] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-gray-900 mb-1">{category.name}</h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {category.subcategories?.slice(0, 3).join(", ")}
                      {category.subcategories?.length > 3 && ` +${category.subcategories.length - 3} more`}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No categories found matching "{searchQuery}"</p>
          </div>
        )}

        {/* Stats */}
        <div className="mt-12 text-center">
          <p className="text-gray-500">
            Showing {filteredCategories.length} of {categories.length} categories
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
